import { ajvResolver } from '@hookform/resolvers/ajv'
import { useGoogleLogin } from '@react-oauth/google'
import { useAlert } from 'contexts/alert'
import { useAuth } from 'contexts/auth'
import { useCustomTheme } from 'contexts/theme'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { destinoAposLogin } from 'hooks/destino-inicial'
import { useHistory, useLocation } from 'react-router-dom'
import SignInSchema, { ISignInFormData } from 'schema/authentication/sign-in'
import api from 'services/api'

const useComponent = (token?: string) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingGoogle, setIsLoadingGoogle] = useState(false)
  const [confirmEmail, setConfirmEmail] = useState('')
  const [showMessage, setShowMessage] = useState(false)
  const [isPasswordHidden, setIsPasswordHidden] = useState(true)

  const { signIn, signInWithGoogle } = useAuth()
  const { closeAlert, openAlert } = useAlert()
  const { theme } = useCustomTheme()
  const history = useHistory()
  const location = useLocation<{ from?: unknown } | undefined>()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ISignInFormData>({
    resolver: ajvResolver(SignInSchema),
  })

  const handleMessageClose = () => {
    setShowMessage(false)
  }

  const login = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      await onSubmitGoogleAuth(tokenResponse?.access_token)
      setIsLoadingGoogle(false)
    },
    onError: () => {
      setIsLoadingGoogle(false)
      openAlert('Algo deu Erradom tente novamente mais tarde!', 'error')
    },
    onNonOAuthError: () => setIsLoadingGoogle(false),
    error_callback: () => setIsLoadingGoogle(false),
  })

  const onSubmitGoogleAuth = useCallback(
    async (code: string) => {
      try {
        setIsLoading(true)

        const user = await signInWithGoogle(code)

        setIsLoading(false)

        const destino = destinoAposLogin(user, location.state?.from)
        if (destino) {
          return history.push(destino)
        }

        const validateRequestPermissions = await api.get('/organizations-permissions/user/validate')

        if (validateRequestPermissions.data.data && user.organization?.certificateValidate !== null) {
          return history.push('/home')
        }

        history.push('/redirect')
      } catch (err: any) {
        setIsLoading(false)
        if (err.statusCode === 403) {
          openAlert(err.data.message, 'error')
        } else {
          openAlert('Erro ao acessar. Tente novamente mais tarde!', 'error')
        }
      }
    },
    [signInWithGoogle, history, location]
  )

  const onSubmit = useCallback(
    async (data: ISignInFormData) => {
      try {
        setIsLoading(true)

        const user = await signIn({
          email: data.email,
          password: data.password,
        })

        setIsLoading(false)

        history.push(destinoAposLogin(user, location.state?.from) ?? '/redirect')
      } catch (err: any) {
        setIsLoading(false)
        if (err.statusCode === 403) {
          openAlert(err.data.message, 'error')
        } else {
          openAlert('Erro ao acessar. Tente novamente mais tarde!', 'error')
        }
      }
    },
    [signIn, history, location]
  )

  const loadData = useCallback(async () => {
    try {
      if (token) {
        const response = await api.patch(`/passwords/confirm-email/${token}`)

        const { data } = response.data

        setConfirmEmail(data.email)
        setShowMessage(true)
      }
    } catch (error) {
      console.log(error)
    }
  }, [setConfirmEmail, setShowMessage, token])

  useEffect(() => {
    loadData()
  }, [])

  const onHandleSubmit = handleSubmit(onSubmit)

  return {
    onHandleSubmit,
    showMessage,
    handleMessageClose,
    confirmEmail,
    errors,
    isLoading,
    register,
    isPasswordHidden,
    setIsPasswordHidden,
    isLoadingGoogle,
    login,
    setIsLoadingGoogle,
  }
}

export default useComponent
